import { Div, Label, Loader } from "@grassrootly/rn-core";
import { GamesListItem } from "../../../components/game/games-list";
import type { IPlayerGame } from "@grassrootly/api";
import { MainStackScreenProps } from "../../../navigation/Navigation.types";
import { GetPlayerPreviousGames, GetPlayerPreviousGamesPayload } from "@grassrootly/api";
import useBaseRequest from "../../../api/BaseRequest";
import { useEffect, useState, useRef } from "react";
import moment from "moment";

interface PlayerMatchesProps {
  playerId: number;
  navigation: MainStackScreenProps<"eqipa-card">["navigation"];
}

export default function PlayerMatches({ playerId, navigation }: PlayerMatchesProps) {
  const [gamesOffset, setGamesOffset] = useState<number>(0);
  const [hasMoreGames, setHasMoreGames] = useState<boolean>(true);
  const [allGames, setAllGames] = useState<IPlayerGame[]>([]);
  const [currentPlayerId, setCurrentPlayerId] = useState<number | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  const handleGamePress = (gameId: number) => {
    navigation.navigate("game", { id: gameId });
  };

  const {
    loading,
    execute: getGamesRequest,
  } = useBaseRequest(GetPlayerPreviousGames, {
    onCompleted: (data) => {
      if (!data) return;
      
      // Check if this is a new player or initial load
      if (isInitialLoad || currentPlayerId !== playerId) {
        // First load or new player - replace all games
        setAllGames(data);
        setCurrentPlayerId(playerId);
        setGamesOffset(data.length); // Set offset to number of items loaded
        setIsInitialLoad(false);
      } else {
        // Pagination load - append games
        const newGames = [...allGames, ...data];
        setAllGames(newGames);
        setGamesOffset(prev => prev + data.length); // Increment offset by items loaded
      }
      
      setHasMoreGames(data.length === 10); // If we got less than 10, no more games
    },
  });

  // Use the same request handler for both initial load and pagination
  const getGames = getGamesRequest;
  const loadingMore = loading;

  const handleLoadMoreGames = () => {
    if (hasMoreGames && !loadingMore && playerId) {
      const now = moment().toISOString();
      setIsInitialLoad(false); // Ensure this is treated as pagination
      getGames({ 
        date_to: now, 
        pid: playerId,
        limit: 10, 
        offset: gamesOffset 
      });
    }
  };

  // Set up intersection observer for auto-loading
  useEffect(() => {
    if (!loadMoreTriggerRef.current || !hasMoreGames || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          handleLoadMoreGames();
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        rootMargin: '100px', // Start loading 100px before the element comes into view
      }
    );

    observer.observe(loadMoreTriggerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasMoreGames, loadingMore, handleLoadMoreGames]);

  useEffect(() => {
    if (playerId && playerId !== currentPlayerId) {
      // Reset pagination and load first page when player changes
      setGamesOffset(0);
      setHasMoreGames(true);
      setAllGames([]);
      setCurrentPlayerId(playerId);
      setIsInitialLoad(true);
      const now = moment().toISOString();
      getGames({ date_to: now, pid: playerId, limit: 10, offset: 0 });
    }
  }, [playerId, currentPlayerId]);

  return (
    <Div classNames={["w100", "maxW-{420}", "ml-{auto}", "mr-{auto}"]}>
      {loading && allGames.length === 0 && (
        <Div classNames={["alignItemsCenter", "mt-{30}"]}>
          <Loader size="large" isLoading />
        </Div>
      )}
      
      {allGames && allGames.length > 0 ? (
        <>
          {allGames.map((game: IPlayerGame, index: number) => (
            <Div key={`game-${game.game_id || index}`} classNames={["mb-{12}"]}>
              <GamesListItem 
                game={game} 
                showGroupName={true} 
                onGamePress={handleGamePress}
              />
            </Div>
          ))}
          
          {/* Auto-load trigger element */}
          {hasMoreGames && (
            <div 
              ref={loadMoreTriggerRef}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                marginTop: '16px', 
                marginBottom: '16px',
                minHeight: '20px'
              }}
            >
              {loadingMore && <Loader size={24} />}
            </div>
          )}
        </>
      ) : (
        !loading && <Label type="l3Regular">No previous games to display</Label>
      )}
    </Div>
  );
}
